import {
  Users,
  Heart,
  Truck,
  MessageSquare,
  DollarSign,
  Briefcase,
  Handshake,
  Settings,
  Wallet,
  Gift,
  Target,
  Smile,
  Frown,
  Zap,
  Package,
  Building2,
  Shuffle,
  Sparkles,
  AlertCircle,
  Clock,
  BarChart3,
  AlertTriangle,
  User,
  Cloud,
  Sun,
  Star,
  type LucideIcon,
} from 'lucide-react'
import { NAMESPACES } from '@/lib/ontology/namespaces'

const { market, business, solution } = NAMESPACES

// Canvas section definition
export interface CanvasSection {
  id: string
  title: string
  icon: LucideIcon
  color: string
  bgColor: string
  classUris: string[]
  gridArea: string
  description: string
  sectionType?: 'default' | 'photo' // Optional section type for special rendering
}

// Canvas template definition
export interface CanvasTemplate {
  id: string
  name: string
  description: string
  gridColumns: number
  gridRows: number
  gridAreas: string
  sections: CanvasSection[]
}

// ============================================
// Business Model Canvas
// ============================================
export const BUSINESS_MODEL_CANVAS: CanvasTemplate = {
  id: 'business-model-canvas',
  name: 'Business Model Canvas',
  description: 'Strategic management template for developing new or documenting existing business models',
  gridColumns: 10,
  gridRows: 6,
  gridAreas: `
    "partners partners activities activities value value relationships relationships segments segments"
    "partners partners activities activities value value relationships relationships segments segments"
    "partners partners resources resources value value channels channels segments segments"
    "partners partners resources resources value value channels channels segments segments"
    "costs costs costs costs costs revenue revenue revenue revenue revenue"
    "costs costs costs costs costs revenue revenue revenue revenue revenue"
  `,
  sections: [
    {
      id: 'key-partners',
      title: 'Key Partners',
      icon: Handshake,
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}KeyPartnership`],
      gridArea: 'partners',
      description: 'Network of suppliers and partners that make the business model work',
    },
    {
      id: 'key-activities',
      title: 'Key Activities',
      icon: Settings,
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}KeyActivity`],
      gridArea: 'activities',
      description: 'Most important things a company must do to make its business model work',
    },
    {
      id: 'key-resources',
      title: 'Key Resources',
      icon: Briefcase,
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}KeyResource`],
      gridArea: 'resources',
      description: 'Most important assets required to make a business model work',
    },
    {
      id: 'value-propositions',
      title: 'Value Propositions',
      icon: Heart,
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${business}ValueProposition`],
      gridArea: 'value',
      description: 'Bundle of products and services that create value for a specific Customer Segment',
    },
    {
      id: 'customer-relationships',
      title: 'Customer Relationships',
      icon: MessageSquare,
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}CustomerRelationship`],
      gridArea: 'relationships',
      description: 'Types of relationships a company establishes with specific Customer Segments',
    },
    {
      id: 'channels',
      title: 'Channels',
      icon: Truck,
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}Channel`],
      gridArea: 'channels',
      description: 'How a company communicates with and reaches its Customer Segments',
    },
    {
      id: 'customer-segments',
      title: 'Customer Segments',
      icon: Users,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}CustomerSegment`, `${market}Persona`],
      gridArea: 'segments',
      description: 'Different groups of people or organizations an enterprise aims to reach and serve',
    },
    {
      id: 'cost-structure',
      title: 'Cost Structure',
      icon: Wallet,
      color: '#847561',
      bgColor: '#f5f4f0',
      classUris: [`${business}CostStructure`],
      gridArea: 'costs',
      description: 'All costs incurred to operate a business model',
    },
    {
      id: 'revenue-streams',
      title: 'Revenue Streams',
      icon: DollarSign,
      color: '#847561',
      bgColor: '#f5f4f0',
      classUris: [`${business}RevenueStream`],
      gridArea: 'revenue',
      description: 'Cash a company generates from each Customer Segment',
    },
  ],
}

// ============================================
// Value Proposition Canvas
// ============================================
export const VALUE_PROPOSITION_CANVAS: CanvasTemplate = {
  id: 'value-proposition-canvas',
  name: 'Value Proposition Canvas',
  description: 'Tool to ensure product-market fit by mapping customer needs to value creation',
  gridColumns: 12,
  gridRows: 6,
  gridAreas: `
    "gains gains gains gains gains gains vp vp vp vp vp vp"
    "gains gains gains gains gains gains vp vp vp vp vp vp"
    "jobs jobs jobs jobs jobs jobs products products products products products products"
    "jobs jobs jobs jobs jobs jobs products products products products products products"
    "pains pains pains pains pains pains relievers relievers relievers relievers relievers relievers"
    "pains pains pains pains pains pains relievers relievers relievers relievers relievers relievers"
  `,
  sections: [
    // Customer Profile (left side)
    {
      id: 'customer-jobs',
      title: 'Customer Jobs',
      icon: Target,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}CustomerJob`, `${market}Need`],
      gridArea: 'jobs',
      description: 'Tasks customers are trying to perform, problems they are trying to solve, or needs they want to satisfy',
    },
    {
      id: 'customer-gains',
      title: 'Gains',
      icon: Smile,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Gain`, `${market}Benefit`],
      gridArea: 'gains',
      description: 'Outcomes and benefits customers want - functional utility, social gains, positive emotions, cost savings',
    },
    {
      id: 'customer-pains',
      title: 'Pains',
      icon: Frown,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Pain`, `${market}Frustration`],
      gridArea: 'pains',
      description: 'Bad outcomes, risks, and obstacles related to customer jobs - undesired costs, negative emotions, risks',
    },
    // Value Map (right side)
    {
      id: 'value-propositions',
      title: 'Value Proposition',
      icon: Heart,
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${business}ValueProposition`],
      gridArea: 'vp',
      description: 'The bundle of products and services that create value for a specific customer segment',
    },
    {
      id: 'products-services',
      title: 'Products & Services',
      icon: Gift,
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${solution}Product`, `${solution}Service`, `${solution}Feature`],
      gridArea: 'products',
      description: 'List of all products and services a value proposition is built around',
    },
    {
      id: 'pain-relievers',
      title: 'Pain Relievers',
      icon: Zap,
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${solution}PainReliever`, `${solution}Solution`],
      gridArea: 'relievers',
      description: 'How products and services alleviate customer pains - eliminate, reduce or minimize pains',
    },
  ],
}

// ============================================
// Positioning Canvas (April Dunford)
// ============================================
export const POSITIONING_CANVAS: CanvasTemplate = {
  id: 'positioning-canvas',
  name: 'Positioning Canvas',
  description: 'April Dunford\'s positioning framework for defining how your product wins in the market',
  gridColumns: 4,
  gridRows: 6,
  gridAreas: `
    "product product product product"
    "product product product product"
    "category category category category"
    "alternatives attributes value whocares"
    "alternatives attributes value whocares"
    "alternatives attributes value whocares"
  `,
  sections: [
    {
      id: 'product',
      title: 'Product',
      icon: Package,
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${solution}Product`, `${solution}Service`],
      gridArea: 'product',
      description: 'Product name and one-line description - what you are positioning',
    },
    {
      id: 'market-category',
      title: 'Market Category',
      icon: Building2,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Industry`, `${market}MarketCategory`],
      gridArea: 'category',
      description: 'The macro market and submarket (if applicable) that you compete in',
    },
    {
      id: 'competitive-alternatives',
      title: 'Competitive Alternatives',
      icon: Shuffle,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Competitor`, `${market}Alternative`],
      gridArea: 'alternatives',
      description: 'What would customers use if your product did not exist?',
    },
    {
      id: 'unique-attributes',
      title: 'Unique Attributes',
      icon: Sparkles,
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${solution}UniqueSellingPoint`, `${solution}Feature`, `${solution}Differentiator`],
      gridArea: 'attributes',
      description: 'What features/capabilities does your product have that the alternatives do not?',
    },
    {
      id: 'value',
      title: 'Value',
      icon: Heart,
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${business}ValueProposition`],
      gridArea: 'value',
      description: 'What value do those attributes enable for customers?',
    },
    {
      id: 'who-cares',
      title: 'Who Cares a Lot',
      icon: Users,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}CustomerSegment`, `${market}Persona`],
      gridArea: 'whocares',
      description: 'What are the characteristics of a customer that makes them care a lot about the value you deliver?',
    },
  ],
}

// ============================================
// Problem Statement Canvas
// ============================================
export const PROBLEM_STATEMENT_CANVAS: CanvasTemplate = {
  id: 'problem-statement-canvas',
  name: 'Problem Statement Canvas',
  description: 'Framework for discovering and refining problem statements before jumping to solutions',
  gridColumns: 6,
  gridRows: 4,
  gridAreas: `
    "context context problem problem alternatives alternatives"
    "context context problem problem alternatives alternatives"
    "customers customers emotional emotional shortcomings shortcomings"
    "customers customers quantifiable quantifiable shortcomings shortcomings"
  `,
  sections: [
    {
      id: 'context',
      title: 'Context',
      icon: Clock,
      color: '#6c757d',
      bgColor: '#f8f9fa',
      classUris: [`${market}Problem`],
      gridArea: 'context',
      description: 'When does the problem occur? What situation triggers it?',
    },
    {
      id: 'problem',
      title: 'Problem',
      icon: AlertCircle,
      color: '#dc3545',
      bgColor: '#ffeef0',
      classUris: [`${market}Problem`],
      gridArea: 'problem',
      description: 'What is the root cause of the problem?',
    },
    {
      id: 'alternatives',
      title: 'Alternatives',
      icon: Shuffle,
      color: '#6c757d',
      bgColor: '#f8f9fa',
      classUris: [`${market}Alternative`, `${market}Competitor`],
      gridArea: 'alternatives',
      description: 'What do customers do now to fix the problem?',
    },
    {
      id: 'customers',
      title: 'Customers',
      icon: Users,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}CustomerSegment`, `${market}Persona`],
      gridArea: 'customers',
      description: 'Who has the problem most often?',
    },
    {
      id: 'emotional-impact',
      title: 'Emotional Impact',
      icon: Heart,
      color: '#c9a227',
      bgColor: '#faf5e8',
      classUris: [`${market}CustomerPain`],
      gridArea: 'emotional',
      description: 'How does the customer feel?',
    },
    {
      id: 'quantifiable-impact',
      title: 'Quantifiable Impact',
      icon: BarChart3,
      color: '#4a90a4',
      bgColor: '#e6f3f7',
      classUris: [`${market}CustomerPain`],
      gridArea: 'quantifiable',
      description: 'What is the measurable impact (include units)?',
    },
    {
      id: 'alternative-shortcomings',
      title: 'Alternative Shortcomings',
      icon: AlertTriangle,
      color: '#dc3545',
      bgColor: '#ffeef0',
      classUris: [`${market}AlternativeDisadvantage`],
      gridArea: 'shortcomings',
      description: 'What are the disadvantages of the alternatives?',
    },
  ],
}

// ============================================
// Persona Canvas (Design A Better Business style)
// ============================================
export const PERSONA_CANVAS: CanvasTemplate = {
  id: 'persona-canvas',
  name: 'Persona Canvas',
  description: 'Understand your customer deeply - their environment, needs, fears and hopes',
  gridColumns: 6,
  gridRows: 6,
  gridAreas: `
    "negative negative photo photo positive positive"
    "negative negative photo photo positive positive"
    "headaches headaches name name opportunities opportunities"
    "headaches headaches name name opportunities opportunities"
    "fears fears need need hopes hopes"
    "fears fears need need hopes hopes"
  `,
  sections: [
    {
      id: 'negative-trends',
      title: 'Negative Trends',
      icon: Cloud,
      color: '#847561',
      bgColor: '#f5f4f0',
      classUris: [`${market}MarketTrend`, `${market}Threat`],
      gridArea: 'negative',
      description: 'Negative trends from the environment affecting this persona',
    },
    {
      id: 'positive-trends',
      title: 'Positive Trends',
      icon: Sun,
      color: '#847561',
      bgColor: '#f5f4f0',
      classUris: [`${market}MarketTrend`, `${market}Opportunity`],
      gridArea: 'positive',
      description: 'Positive trends from the environment benefiting this persona',
    },
    {
      id: 'photo',
      title: 'Photo',
      icon: User,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Persona`],
      gridArea: 'photo',
      description: 'Visual representation of the persona',
      sectionType: 'photo',
    },
    {
      id: 'name-role',
      title: 'Name & Role',
      icon: User,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Persona`],
      gridArea: 'name',
      description: 'The persona name and their role/job title',
    },
    {
      id: 'headaches',
      title: 'Headaches',
      icon: Frown,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Pain`, `${market}Frustration`],
      gridArea: 'headaches',
      description: 'Professional and work related issues they face',
    },
    {
      id: 'opportunities',
      title: 'Opportunities',
      icon: Smile,
      color: '#5c8a6b',
      bgColor: '#e8f2ea',
      classUris: [`${market}Opportunity`, `${market}Gain`],
      gridArea: 'opportunities',
      description: 'Professional and work related positive outcomes they seek',
    },
    {
      id: 'fears',
      title: 'Fears',
      icon: AlertTriangle,
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${market}Fear`, `${market}Risk`],
      gridArea: 'fears',
      description: 'Personal fears and anxieties',
    },
    {
      id: 'need',
      title: 'Need',
      icon: Heart,
      color: '#c9a857',
      bgColor: '#faf5e8',
      classUris: [`${market}Need`, `${market}CustomerJob`],
      gridArea: 'need',
      description: 'What does this person really want? Their core need.',
    },
    {
      id: 'hopes',
      title: 'Hopes',
      icon: Star,
      color: '#3d7a8c',
      bgColor: '#e6f3f7',
      classUris: [`${market}Hope`, `${market}Aspiration`],
      gridArea: 'hopes',
      description: 'Personal goals and hopes for the future',
    },
  ],
}

// ============================================
// All available canvas templates
// ============================================
export const CANVAS_TEMPLATES: CanvasTemplate[] = [
  BUSINESS_MODEL_CANVAS,
  VALUE_PROPOSITION_CANVAS,
  POSITIONING_CANVAS,
  PROBLEM_STATEMENT_CANVAS,
  PERSONA_CANVAS,
]

// Get a canvas template by ID
export function getCanvasTemplate(id: string): CanvasTemplate | undefined {
  return CANVAS_TEMPLATES.find((t) => t.id === id)
}
